import React, { FC } from 'react';
import Icon from '@minecraft.icon';
import {
  BasicContainer,
  BodyText,
  BodyTextSmall,
  CustomBorderProps,
  FlexCol,
  FlexDynamic,
  FlexRow,
  H1Text,
  H4Text,
  Ribbon,
} from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { PlanType } from '../usePlans';
import { BillingCodes } from '../enums/plans';
import { FeatureList } from './FeatureList';
import { PlanButton } from './PlanButton';
import { PlanSelectionProps } from './types';
import { PlanPriceSection } from './PlanPriceSection';
import { generateUniqueId } from '../helpers/functions';
import { FreePlanLegalText } from '../Checkout/Legal';

export const Plan: FC<PlanSelectionProps> = React.memo(
  ({
    plans,
    countryCode,
    setSelectedPlan,
    onNext,
    paywallLocation,
    isTrialEligible,
    isAnnual,
    onCompletePurchase,
    onPaymentCompleteRedirect,
  }) => {
    const { t } = useTranslation();
    const { showMessage } = useGlobalNotification();

    const showablePlans = plans.filter((plan) => !plan.shouldHidePlan);

    const goToNextStep = async (plan: PlanType) => {
      if (plan.billingCode === BillingCodes.registration) {
        try {
          await onCompletePurchase({ isRedirectToOpenCall: true, termsAccepted: true });
          onPaymentCompleteRedirect('login');
        } catch (error) {
          showMessage({
            message: t('common:paywall.payment.error'),
            color: 'danger',
          });
        }
        return;
      }

      setSelectedPlan(plan);
      onNext();
    };

    const getPlanBorderSizeTop = (isFreePlan: boolean, isHotTrial: boolean) => {
      if (isFreePlan) return 0.15;
      if (isHotTrial) return 2.5;
      return 0.7;
    };

    return (
      <FlexDynamic
        direction="column"
        gap="0"
        mdProps={{ direction: 'row', gap: '4' }}
        wrap="nowrap"
        justifyContent={showablePlans.length === 1 ? 'center' : 'space-between'}
      >
        {showablePlans.map((plan) => {
          const customBorder = {
            borderColor: `${plan.temperature}Paywall`,
            borderSize: 0.15,
            borderStyle: 'solid',
          } as CustomBorderProps;
          if (plan.shouldHidePlan) return null;
          const uniqueId = generateUniqueId({ plan, paywallLocation });
          const isHotTrial = isTrialEligible && plan.isEssentialTrialPlan;
          const isFreePlan = plan.billingCode === BillingCodes.registration;

          return (
            <FlexCol
              alignItems={showablePlans.length >= 2 ? 'center' : 'space-between'}
              gap="8"
              key={plan.billingCode}
              className={`${plan.billingCode === BillingCodes.registration ? 'cn_w-fit' : 'cn_w-full'}`}
              minWidth={20.5}
              maxWidth={33.5}
              borderRadius="md"
              bgColor="card"
            >
              <FlexCol
                borderColor={`${plan.temperature}Paywall`}
                borderSize={0.1}
                borderRadius="md"
                borderPlacement="all"
                customBorders={{
                  top: {
                    borderColor: isHotTrial ? `${plan.temperature}TrialUpsellPaywall` : `${plan.temperature}Paywall`,
                    borderSize: getPlanBorderSizeTop(isFreePlan, isHotTrial),
                    borderStyle: 'solid',
                  } as CustomBorderProps,
                  left: customBorder,
                  right: customBorder,
                  bottom: {
                    borderColor: `${plan.temperature}Paywall`,
                    borderSize: isFreePlan ? 0.15 : 0.2,
                    borderStyle: 'solid',
                  },
                }}
                className={`cn_w-full cn_atom_px-2 cn_atom_py-4 cn_atom_mb-4 cn_relative cn_gradient-border-${plan.gradientColor}`}
                data-testid={`plan-option-container-${plan.billingCode}`}
              >
                {plan.discountOfferText && (
                  <BasicContainer
                    bgColor="coolPaywallSuccessBg"
                    className="cn_absolute cn_atom_pb-2"
                    w="full"
                    alignItems="center"
                    justifyItems="center"
                    top={0}
                    left={0}
                  >
                    <H4Text fontWeight="bold" color="emphasis">
                      {t(plan.discountOfferText)}
                    </H4Text>
                  </BasicContainer>
                )}
                {plan.discountPercent && (
                  <Ribbon bgColor="paywallBestDealBg" direction="right" top={1}>
                    <H4Text fontWeight="bold" color="emphasis">
                      {t('common:paywall.plan.discount', { discountPercent: plan.discountPercent })}
                    </H4Text>
                  </Ribbon>
                )}
                {plan.isTrialPeriodEnabled && plan.isEssentialTrialPlan && (
                  <BasicContainer className="cn_w-full cn_absolute" top={-2}>
                    <BodyText color="inverted" align="center" fontWeight="medium" className="cn_w-full">
                      {t(`common:paywall.plan.trial.upsellMessage`, { days: plan.trialLengthDays })}
                    </BodyText>
                  </BasicContainer>
                )}
                <FlexCol alignItems="center" justifyContent="center" gap="3" className="cn_m-auto cn_mt-6">
                  <FlexRow alignItems="center" justifyContent="center" gap="2">
                    {plan.icon && <Icon name={plan.icon} themeColor={`${plan.temperature}Paywall`} />}
                    <H1Text>{plan.title}</H1Text>
                  </FlexRow>
                  <FlexCol alignItems="center" justifyContent="center" mb="3">
                    <PlanPriceSection
                      isAnnual={isAnnual}
                      countryCode={countryCode}
                      isTrialPeriodEnabled={plan.isTrialPeriodEnabled}
                      billingCode={plan.billingCode}
                      billed={plan.billed}
                      currencyCode={plan.currencyCode}
                      planTermCode={plan.term?.code}
                      price={Number(plan.price)}
                      strikeThroughPrice={Number(plan.strikeThroughPrice)}
                    />
                  </FlexCol>
                  <PlanButton plan={plan} goToNextStep={goToNextStep} t={t} uniqueId={uniqueId} />
                </FlexCol>
                <FeatureList
                  temperature={plan.temperature}
                  features={plan.keyFeatures}
                  iconName={plan.iconName}
                  including={plan.including}
                  className="cn_mt-8"
                  justifyContent="center"
                />
                {plan?.isTrialPeriodEnabled && (
                  <BasicContainer className="cn_mx-8">
                    <BodyTextSmall color="disabled" align="center" className="cn_atom_pb-1 cn_atom_pt-3">
                      {t('common:paywall.plan.trial.legal.cancelTime', { trialLengthDays: plan.trialLengthDays ?? 30 })}
                    </BodyTextSmall>
                  </BasicContainer>
                )}
                {isFreePlan && <FreePlanLegalText />}
              </FlexCol>
            </FlexCol>
          );
        })}
      </FlexDynamic>
    );
  }
);
